import { getCookie } from '@alexis/helpers/cookie';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';

import { updateUserV3 } from '@apis/userV3';

import Toast from '@components/Toast/Toast';
import TravellerInfoForm from '@components/TravellerInfoForm';
import { TravellerInfoFormSubmitData } from '@components/TravellerInfoForm/types';

import { getNationalIdDoc, getPassportDoc } from '@helpers/traveller';

import { changeUser } from '@redux/actions/userActions';
import { changeUserV3 } from '@redux/actions/userV3Actions';
import {
  getDomainBasedApiBaseUrlState,
  getTranslationsState,
  getUserState,
  getUserV3State,
} from '@redux/selectors';

import { User } from '@wegoTypes/user';

import styles from './MyPersonalInfo.module.scss';

const MyPersonalInfo = () => {
  const userV3 = useSelector(getUserV3State);
  const translations = useSelector(getTranslationsState);
  const dispatch = useDispatch();
  const domainBasedApiBaseUrl: string = useSelector(getDomainBasedApiBaseUrlState);
  const user = useSelector(getUserState);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { pageViewId } = useOutletContext<{ pageViewId: string }>();
  if (!userV3) {
    return null;
  }

  const handleGenzoActionEvent = () => {
    try {
      const wegoAnalyticsClientIdCookie = getCookie('wego_analytics_client_id');
      const wegoAnalyticsClientSessionIdCookie = getCookie('wego_analytics_client_session_id');
      if (!!wegoAnalyticsClientIdCookie && !!wegoAnalyticsClientSessionIdCookie) {
        const eventData = {
          id: pageViewId,
          category: 'personal_info',
          object: 'save_form',
          action: 'click',
          value: '',
        };
        genzoTrackActionEvent(
          API_BASE_URL,
          wegoAnalyticsClientIdCookie,
          wegoAnalyticsClientSessionIdCookie,
          user?.userHash,
          eventData,
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleFormSubmission = async (data: TravellerInfoFormSubmitData) => {
    try {
      handleGenzoActionEvent();
      setIsSubmitting(true);
      const userV3 = await updateUserV3(domainBasedApiBaseUrl, {
        profile: {
          firstName: data.firstName,
          lastName: data.lastName,
          nationality: data.nationality,
          phoneNumber: data.phoneNumber,
          phoneCountryCode: data.phoneCountryCode,
        },
        primaryTraveller: {
          firstName: data.firstName,
          lastName: data.lastName,
          nationality: data.nationality,
          gender: data.gender,
          dateOfBirth: data.dateOfBirth,
          travellerType: 'ADULT',
          travellerDocuments: [
            ...(data.passportNumber
              ? [
                  {
                    documentNumber: data.passportNumber,
                    documentType: 'PASSPORT' as const,
                    expiryDate: data.passportExpiryDate,
                    issuingCountry: data.nationality,
                  },
                ]
              : []),
            ...(data.nationalIdNumber
              ? [
                  {
                    documentNumber: data.nationalIdNumber,
                    documentType: 'NATIONAL_ID' as const,
                    expiryDate: data.nationalIdExpiryDate,
                    issuingCountry: data.nationality,
                  },
                ]
              : []),
          ],
        },
      });
      dispatch(changeUserV3(userV3));
      dispatch(
        changeUser({
          ...user,
          displayName: !!userV3.profile.name?.trim() ? userV3.profile.name : user?.displayName,
          firstName: userV3.profile.firstName || user?.firstName,
          lastName: userV3.profile.lastName || user?.lastName,
          nationality: userV3.profile.nationality || user?.nationality,
          phoneNumber: userV3.profile.phoneNumber
            ? parseInt(userV3.profile.phoneNumber)
            : user?.phoneNumber,
          phoneCountryCode: userV3.profile.phoneCountryCode || user?.phoneCountryCode,
          email: userV3.profile.email || user?.email,
          userHash: userV3.profile.userHash || user?.userHash,
        } as User),
      );
      setToastMessage(translations.information_updated as string);
    } catch (error) {
      setToastMessage(translations.something_wrong_try_again as string);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.heading}>{translations.personal_info}</div>
      <TravellerInfoForm
        onSubmit={handleFormSubmission}
        traveller={
          userV3
            ? {
                firstName: userV3.profile.firstName || undefined,
                lastName: userV3.profile.lastName || undefined,
                nationality: userV3.profile.nationality || undefined,
                gender: userV3.primaryTraveller?.gender,
                email: userV3.profile.email || undefined,
                dateOfBirth: userV3.primaryTraveller?.dateOfBirth,
                phoneNumber: userV3.profile.phoneNumber || undefined,
                phoneCountryCode: userV3.profile.phoneCountryCode || undefined,
                passportNumber: getPassportDoc(userV3.primaryTraveller?.travellerDocuments)
                  ?.documentNumber,
                passportExpiryDate:
                  getPassportDoc(userV3.primaryTraveller?.travellerDocuments)?.expiryDate ||
                  undefined,
                nationalIdNumber: getNationalIdDoc(userV3.primaryTraveller?.travellerDocuments)
                  ?.documentNumber,
                nationalIdExpiryDate:
                  getNationalIdDoc(userV3.primaryTraveller?.travellerDocuments)?.expiryDate ||
                  undefined,
              }
            : undefined
        }
        isSubmitting={isSubmitting}
        isPrimary={true}
      />
      <Toast message={toastMessage} setMessage={setToastMessage} />
    </div>
  );
};

export default MyPersonalInfo;
